import {Chip, ChipProps, Stack, Typography} from "@mui/material";
import React from "react";
import {Link} from "react-router-dom";
import {Location, LocationAncestry, LocationType} from "../../api";
import {useLocalization} from "@smalox/shared";
import {useTenantId} from "../../tenant";
import {LocationIcon} from "./LocationIcon";
import {formatLocationPath} from "./path";

interface LocationChipProps extends Pick<ChipProps, "color" | "variant" | "onDelete"> {
	location: Location | LocationAncestry;
}

export function LocationChip({location, ...chipProps}: LocationChipProps) {
	return (
		<Chip
			icon={<LocationIcon type={location.type} />}
			label={<LocationChipLabel location={location} />}
			{...chipProps}
		/>
	);
}

interface LocationLinkChipProps {
	location: Location | LocationAncestry;
	ancestry?: boolean;
}

export function LocationLinkChip({location, ancestry = true, ...chipProps}: LocationLinkChipProps) {
	const tenantId = useTenantId();
	return (
		<Chip
			icon={<LocationIcon type={location.type} />}
			label={<LocationChipLabel location={location} ancestry={ancestry} />}
			component={Link}
			to={`/${tenantId}/locations/${location.id}`}
			variant="outlined"
			color="secondary"
			clickable
			{...chipProps}
		/>
	);
}

function LocationChipLabel({location, ancestry = true}: LocationLinkChipProps) {
	const path = formatLocationPath(location.parent);
	return (
		<Stack direction="row" alignItems="center" spacing={2}>
			<Typography variant="body1">
				{location.name}
			</Typography>
			{ancestry && (
				<Typography variant="body2" color="textSecondary">
					{path}
				</Typography>
			)}
		</Stack>
	);
}

export function LocationTypeChip({locationType}: { locationType: LocationType }) {
	const {t} = useLocalization();
	return (
		<Chip
			icon={<LocationIcon type={locationType} />}
			label={t(`locations.types.${locationType}`)}
			variant="outlined"
			color="default"
		/>
	);
}
